/**
 * The certification boards' register, read from the library (build 2).
 *
 * Renders nothing; sits in the shell. On the platform, the moment the engine
 * is here it is told the register is the library's — the sample is emptied
 * before any board can draw it — and the certificates the library holds are
 * installed as they arrive. It also hands the engine what only the platform
 * can do for it: read a certificate's history, open an issue's file, and say
 * what this person may do, so the boards disable a button with its reason
 * instead of failing after it is pressed.
 *
 * Under the mock it reads nothing and leaves the sample as it is, but gives
 * the demo back what this browser recorded before a reload.
 *
 * Like FlightsFeed it costs nothing until a board is open: its read waits for
 * the engine, and it never causes the engine to load.
 */
import { useEffect, useRef, useState } from "react";
import { useSession } from "../../../app/SessionContext";
import { DataSourceError, hasPermission, PERMISSION } from "../../../api/dataSource";
import { useCertificateRegister } from "../../../api/certificatesQueries";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";

/** The merge and the tables it reads, fetched only when there is something to merge. */
const merger = () => import("../mergeCertificates");

export function CertificatesFeed() {
  const { source, site, principal } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);

  /*
   * Synchronously on arrival, inside the loader's own queue: the board that
   * caused the load draws right after it, and it must not draw the sample on
   * the live site even once.
   */
  useEffect(() => {
    let gone = false;
    whenDemoLoaded((d) => {
      if (gone) return;
      if (live) d.certGoLive(false);
      else d.certRestoreLocal();
      setEngine(true);
    });
    return () => {
      gone = true;
    };
  }, [live]);

  /* another site's certificates never stand in while this one's load */
  const siteRef = useRef(site.id);
  useEffect(() => {
    if (siteRef.current === site.id) return;
    siteRef.current = site.id;
    if (live) demoIfLoaded()?.certGoLive(true);
  }, [live, site.id]);

  useEffect(() => {
    if (!engine) return;
    demoIfLoaded()?.setCertFeed(
      live
        ? {
            host: {
              history: (id: string) => source.getDocument(id),
              open: (revisionId: string) => source.downloadRevision(revisionId),
            },
            can: {
              write: hasPermission(principal, PERMISSION.documentsWrite),
              approve: hasPermission(principal, PERMISSION.documentsApprove),
              work: hasPermission(principal, PERMISSION.workOrdersWrite),
            },
          }
        : { host: null },
    );
  }, [engine, live, source, principal]);

  const register = useCertificateRegister({ enabled: live && engine });
  const rows = register.data;
  const error = register.error;

  useEffect(() => {
    if (!live || !engine) return;
    if (error) {
      const why = error instanceof Error ? error.message : String(error);
      const refused = error instanceof DataSourceError && (error.status === 401 || error.status === 403);
      void merger().then((m) => (refused ? m.mergeCertificates([], why) : m.certificatesUnread(why)));
      return;
    }
    if (rows) void merger().then((m) => m.mergeCertificates(rows));
  }, [live, engine, rows, error]);

  return null;
}
